import React from 'react';
import { FiUsers, FiCheckCircle, FiClock, FiShield, FiZap, FiMail, FiGlobe, FiSmartphone, FiHeart } from 'react-icons/fi';

const ParentAdmissionPortal = () => {
  const reasons = [
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Convenience of Applying from Anywhere',
      description: 'Parents and students can apply from home, work, or anywhere with an internet connection. No more taking time off work, traveling to campus, or standing in long queues. The application process fits into their schedule, not the other way around.',
      stat: '85% of parents prefer online applications for convenience',
      solution: 'Truyon enables 24/7 applications from any device, anywhere.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiSmartphone className="w-6 h-6" />,
      title: 'Mobile-Friendly Experience',
      description: 'Most parents and students complete applications on their phones. Institutions with mobile-friendly portals make it easy to apply on the go. No zooming, no scrolling issues, no frustration. A seamless experience on any device.',
      stat: '78% of applicants use mobile devices for college applications',
      solution: 'Truyon is mobile-first, ensuring a smooth experience on phones and tablets.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Transparency and Real-Time Updates',
      description: 'Parents want to know where their child\'s application stands. Online portals provide real-time visibility—application submitted, documents verified, interview scheduled, offer made. No more anxious waiting and guessing.',
      stat: '90% of parents value real-time application status updates',
      solution: 'Truyon provides real-time tracking and automated status updates.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiMail className="w-6 h-6" />,
      title: 'Clear Communication Throughout',
      description: 'Online portals enable automated, consistent communication. Parents receive confirmation emails, document reminders, interview schedules, and admission offers without any gaps. They feel informed and valued throughout the process.',
      stat: '80% of parents say communication quality influences their decision',
      solution: 'Truyon automates communication at every stage of the admission journey.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Security and Privacy of Data',
      description: 'Parents trust institutions that protect their personal information. Online portals with secure data handling and privacy controls give parents peace of mind. They know their child\'s information is safe.',
      stat: '75% of parents are concerned about data privacy in admissions',
      solution: 'Truyon provides enterprise-grade security for all applicant data.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiHeart className="w-6 h-6" />,
      title: 'Positive First Impression',
      description: 'The admission experience sets the tone for the entire parent-institution relationship. A smooth, transparent, digital process signals that the institution is modern, caring, and professional. Parents remember this experience.',
      stat: '70% of parents choose institutions based on admission experience',
      solution: 'Truyon helps create a welcoming, efficient admission experience.',
      color: 'bg-blue-50'
    }
  ];

  const parentNeeds = [
    {
      title: 'Simple Application Process',
      description: 'Parents don\'t want complicated forms. They want a quick, intuitive application process that takes 10 minutes or less.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Clear Document Requirements',
      description: 'Parents want to know exactly what documents are needed, in what format, and what size. No confusion or back-and-forth.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Regular Status Updates',
      description: 'Parents want to know where their application stands at every stage. No waiting and wondering.',
      icon: <FiMail className="w-5 h-5" />
    },
    {
      title: 'Quick Decisions',
      description: 'Parents don\'t want to wait weeks or months. They want answers quickly so they can plan.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Easy Payment Options',
      description: 'Parents want secure, convenient payment options. Multiple methods and instant confirmation.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Personal Support When Needed',
      description: 'Parents appreciate the option to speak with someone when they need help. Digital doesn\'t mean impersonal.',
      icon: <FiUsers className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Choosing the right institution is one of the most important decisions a family makes. For parents, it's about finding the best educational environment for their child. For students, it's about their future. And for institutions, it's about attracting and enrolling the right students.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        But here's a simple truth: <strong className="text-slate-900">the admission experience shapes how parents and students perceive an institution.</strong> A smooth, digital process signals that the institution is modern, caring, and professional. A frustrating, paper-based process signals the opposite.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        In 2026, parents and students expect online admission portals. Here's why they prefer them—and why your institution should offer one.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">85%</div>
          <div className="text-sm text-slate-500">Parents prefer online applications</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">90%</div>
          <div className="text-sm text-slate-500">Value real-time status updates</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">70%</div>
          <div className="text-sm text-slate-500">Choose based on admission experience</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Parents and Students Prefer Online Admission Portals</h2>

      {/* Reasons List */}
      {reasons.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">{item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>How Truyon Helps</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* What Parents Want Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What Parents Really Want</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {parentNeeds.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Parent Experience: Digital vs. Traditional</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Traditional Process</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Visit campus multiple times</span></li>
              <li className="flex items-start gap-2">• <span>Fill out paper forms manually</span></li>
              <li className="flex items-start gap-2">• <span>No status visibility</span></li>
              <li className="flex items-start gap-2">• <span>Anxious waiting</span></li>
              <li className="flex items-start gap-2">• <span>Frustrating experience</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Digital Portal with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Apply from anywhere, anytime</span></li>
              <li className="flex items-start gap-2">• <span>Simple online forms</span></li>
              <li className="flex items-start gap-2">• <span>Real-time status tracking</span></li>
              <li className="flex items-start gap-2">• <span>Regular updates and communication</span></li>
              <li className="flex items-start gap-2">• <span>Positive experience</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Creates the Best Parent Experience</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-Friendly</h4>
            <p className="text-xs text-slate-600">Apply from any device, anywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📝</div>
            <h4 className="font-bold text-slate-900 text-sm">Simple Forms</h4>
            <p className="text-xs text-slate-600">10-minute application process</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💬</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Updates</h4>
            <p className="text-xs text-slate-600">Status tracking and notifications</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📄</div>
            <h4 className="font-bold text-slate-900 text-sm">Clear Guidance</h4>
            <p className="text-xs text-slate-600">Document requirements and support</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💳</div>
            <h4 className="font-bold text-slate-900 text-sm">Easy Payment</h4>
            <p className="text-xs text-slate-600">Secure, multiple payment options</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🤝</div>
            <h4 className="font-bold text-slate-900 text-sm">Personal Support</h4>
            <p className="text-xs text-slate-600">Help available when needed</p>
          </div>
        </div>
      </div>

      {/* The Parent's Perspective */}
      <div className="mt-8 bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
        <h3 className="text-lg font-bold text-emerald-900 mb-3">What Parents Are Saying</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 border border-emerald-200">
            <p className="text-sm text-slate-600 italic">"The online admission portal made everything so easy. I could apply from home, upload documents, and track the status. It saved me so much time and stress."</p>
            <p className="text-xs font-semibold text-emerald-700 mt-2">— Parent of a degree college applicant</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-emerald-200">
            <p className="text-sm text-slate-600 italic">"I loved that I could complete the application on my phone. I didn't need to take time off work. The whole process took less than 15 minutes."</p>
            <p className="text-xs font-semibold text-emerald-700 mt-2">— Parent of a school applicant</p>
          </div>
        </div>
      </div>

      {/* The Student Perspective */}
      <div className="mt-8 bg-blue-50 rounded-2xl p-6 border border-blue-200">
        <h3 className="text-lg font-bold text-blue-900 mb-3">The Student's Experience</h3>
        <p className="text-blue-800 leading-relaxed mb-3">
          While parents manage the application, students experience the institution's digital presence. A modern, well-designed portal signals that the institution values innovation and student experience. It creates excitement and anticipation about joining the community.
        </p>
        <ul className="text-blue-800 leading-relaxed space-y-2">
          <li className="flex items-start gap-2">• <span>Students feel empowered with clear visibility into their application status</span></li>
          <li className="flex items-start gap-2">• <span>Digital portals reduce anxiety by providing clear timelines and next steps</span></li>
          <li className="flex items-start gap-2">• <span>Positive online experience translates to positive perceptions of the institution</span></li>
          <li className="flex items-start gap-2">• <span>Students share their experience with friends, influencing their institution choices</span></li>
        </ul>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Give Parents and Students What They Want</h3>
        <p className="text-indigo-800 leading-relaxed">
          In 2026, online admission portals aren't just nice to have—they're expected. Parents and students choose institutions that make the application process easy, transparent, and convenient.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon helps you deliver the admission experience parents and students want. Mobile-friendly applications, real-time tracking, automated communication, secure payments, and clear guidance—all in one platform.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for online admission portals.</strong> Give parents what they want. Attract more students. Build a stronger reputation.
        </p>
      </div>
    </>
  );
};

export default ParentAdmissionPortal;